import { useMemo } from 'react'

import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'

import {
  fetchSportEntities,
  getFollowableCompetitorsByCompetitionId,
  getFollowableCompetitorsWithNoCompetitionId,
  getSportEntitiesList,
  getSportEntityById,
  getSportEntityBySportradarId,
  isSameSportEntity,
} from '@utils/entities'
import useFeatureFlagsSkeleton from '@hooks/useFeatureFlagsSkeleton'
import { getFmtCompetitionEntityIdsSorted } from '@hooks/useFmtCompetitionEntityIdsSorted'

type DropFirst<T extends any[]> = T extends [any, ...infer Rest] ? Rest : []

type DropFirstAndSecond<T extends any[]> = T extends [any, any, ...infer Rest]
  ? Rest
  : []

type SportEntitiesQueryData = Pick<UseQueryResult, 'error'>

type SportEntitiesUtils = {
  isSameSportEntity: (
    ...args: DropFirst<Parameters<typeof isSameSportEntity>>
  ) => ReturnType<typeof isSameSportEntity>
  getSportEntityById: (
    ...args: DropFirstAndSecond<Parameters<typeof getSportEntityById>>
  ) => ReturnType<typeof getSportEntityById>
  getSportEntityBySportradarId: (
    ...args: DropFirstAndSecond<Parameters<typeof getSportEntityBySportradarId>>
  ) => ReturnType<typeof getSportEntityBySportradarId>
  getSportEntitiesList: (
    ...args: DropFirstAndSecond<Parameters<typeof getSportEntitiesList>>
  ) => ReturnType<typeof getSportEntitiesList>
  getFollowableCompetitorsByCompetitionId: (
    ...args: DropFirstAndSecond<
      Parameters<typeof getFollowableCompetitorsByCompetitionId>
    >
  ) => ReturnType<typeof getFollowableCompetitorsByCompetitionId>
  getFollowableCompetitorsWithNoCompetitionId: (
    ...args: DropFirstAndSecond<
      Parameters<typeof getFollowableCompetitorsWithNoCompetitionId>
    >
  ) => ReturnType<typeof getFollowableCompetitorsWithNoCompetitionId>
}

export type UseSportEntities = () => SportEntitiesQueryData & SportEntitiesUtils

const useSportEntities: UseSportEntities = () => {
  const queryClient = useQueryClient()

  const isFollowingEnabled = useFeatureFlagsSkeleton().isFollowMyTeamEnabled

  const { data: formattedEntities, error } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['sport-entities'],
    queryFn: () =>
      fetchSportEntities({
        isServerRequest: false,
        fmtCompetitionEntityIdsSorted:
          getFmtCompetitionEntityIdsSorted(queryClient),
      }),
  })

  return useMemo(
    () => ({
      error,
      isSameSportEntity: (
        ...args: DropFirst<Parameters<typeof isSameSportEntity>>
      ) => isSameSportEntity(formattedEntities, ...args),
      getSportEntityById: (
        ...args: DropFirstAndSecond<Parameters<typeof getSportEntityById>>
      ) => getSportEntityById(formattedEntities, isFollowingEnabled, ...args),
      getSportEntityBySportradarId: (
        ...args: DropFirstAndSecond<
          Parameters<typeof getSportEntityBySportradarId>
        >
      ) =>
        getSportEntityBySportradarId(
          formattedEntities,
          isFollowingEnabled,
          ...args,
        ),
      getSportEntitiesList: (
        ...args: DropFirstAndSecond<Parameters<typeof getSportEntitiesList>>
      ) => getSportEntitiesList(formattedEntities, isFollowingEnabled, ...args),
      getFollowableCompetitorsByCompetitionId: (
        ...args: DropFirstAndSecond<
          Parameters<typeof getFollowableCompetitorsByCompetitionId>
        >
      ) =>
        getFollowableCompetitorsByCompetitionId(
          formattedEntities,
          isFollowingEnabled,
          ...args,
        ),
      getFollowableCompetitorsWithNoCompetitionId: (
        ...args: DropFirstAndSecond<
          Parameters<typeof getFollowableCompetitorsWithNoCompetitionId>
        >
      ) =>
        getFollowableCompetitorsWithNoCompetitionId(
          formattedEntities,
          isFollowingEnabled,
          ...args,
        ),
    }),
    [formattedEntities, isFollowingEnabled, error],
  )
}

export default useSportEntities
